import i18n from "i18next";
import { initReactI18next } from "react-i18next";

/**
 * AfriGoall i18n dictionaries — EN / FR / ES
 * Keys are grouped by section (nav, hero, landing, register, login, profile, ...).
 * If you add a new key, add it in ALL 3 languages so nothing renders as a raw key.
 */

const en = {
  common: {
    save: "Save", cancel: "Cancel", submit: "Submit", search: "Search", loading: "Loading…",
    delete: "Delete", edit: "Edit", back: "Back", next: "Next", close: "Close", yes: "Yes", no: "No",
    optional: "Optional", required: "Required", or: "or", verified: "Verified",
    country: "Country", city: "City", position: "Position", age: "Age",
    playersCount_one: "{{count}} player", playersCount_other: "{{count}} players",
    viewsCount: "{{count}} views",
  },
  nav: {
    messages: "Messages", players: "Players", pricing: "Pricing", pathway: "Pathway",
    about: "About", signIn: "Sign in", join: "Join now", logout: "Log out",
    dashboard: "Dashboard",
  },
  hero: {
    badge: "African football · global reach",
    line1: "From the pitch",
    line2: "in",
    lagos: "Lagos",
    line3: "to a shirt in",
    madrid: "Madrid",
    subtitle: "AfriGoall is the verified bridge between Africa's youth football talent and professional clubs worldwide. Build a scouting-grade profile in minutes.",
    ctaPlayer: "I'm a player",
    ctaScout: "I'm a scout / club",
    statNations: "Nations",
    statVerified: "Verified",
    statClubs: "Partner clubs",
    featuredOverline: "Featured talent",
    featuredName: "Ibrahim K. — 17y",
    featuredRole: "Right Winger · Ghana",
  },
  landing: {
    valueOverline: "Why AfriGoall",
    valueTitle: "Verified data. Ready for scouts.",
    valueSub: "Every profile is coach-approved and packed with the physical, technical and academic stats scouts actually use.",
    valueCard1Title: "Video highlights",
    valueCard1Body: "Upload your best clips. Every profile plays like a scouting reel.",
    valueCard2Title: "Verified by coaches",
    valueCard2Body: "Coaches and academies rate technical, physical and academic performance.",
    valueCard3Title: "Development pathway",
    valueCard3Body: "Discovery → Trial → Visa/Relocation → Contract. Structured, transparent.",
    countriesOverline: "Top countries",
    countriesTitle: "Talent across the continent.",
    countriesSub: "Verified profiles are already coming in from these nations.",
    testimonial1: '"Bridging African talent to the world." — Head Scout, La Liga',
    testimonial2: '"The verification layer changed everything." — Academy Director, Ghana',
    testimonial3: '"Finally a serious pipeline out of West Africa." — Chief Scout, Bundesliga',
    testimonial4: '"Highlights and radar stats save weeks of work." — Talent ID, Ligue 1',
    testimonial5: '"Made in Africa, watched by the world." — Federation President',
  },
  radar: {
    Speed: "Speed", Stamina: "Stamina", Strength: "Strength",
    Dribble: "Dribble", Shoot: "Shoot", Pass: "Pass", Defend: "Defend",
  },
  toast: {
    cardFailed: "Card generation failed",
    shareUnsupported: "Share not supported here — try Download",
    playerFallback: "Player",
    africaFallback: "Africa",
    shareText: "Check out {{name}} — {{position}} from {{country}} on AfriGoall.",
    shareTitle: "{{name}} on AfriGoall",
  },
  pathway: {
    overline: "The Development Pathway",
    title: "A serious pipeline.",
    subtitle: "From discovery to a professional contract.",
    body: "Every player progresses through a staged pathway. This structure protects players, reduces financial risk, and gives clubs verified data at every step.",
    step1: "Discovery",
    step1Body: "Build a scouting-grade profile with video, stats and verifications.",
    step2: "Trial",
    step2Body: "Scouts invite verified players to formal trials — logged and tracked.",
    step3: "Visa / Relocation",
    step3Body: "Guided paperwork, medicals and travel — coordinated on-platform.",
    step4: "Contract",
    step4Body: "Clubs sign. Success fees settle the pipeline transparently.",
  },
  cta: {
    overline: "Built for a continent",
    title: "Your talent deserves a global stage.",
    createProfile: "Create your profile",
    browsePlayers: "Browse players",
  },
  contact: {
    title: "Get in touch",
    subtitle: "Questions, partnerships, or press? Send us a message.",
    reachOut: "Reach out.",
    name: "Full name", email: "Email", subject: "Subject", message: "Message",
    send: "Send message", sending: "Sending…",
    success: "Message sent — we'll be in touch.",
  },
  spotlights: {
    overline: "Country Spotlights",
    titleA: "Verified talent,",
    titleB: "by nation",
    subtitle: "The best verified profiles on AfriGoall, grouped by their home country. Tap any player to view their full scouting card.",
    verifiedFrom: "Verified from",
    seeAll: "See all",
  },
  trending: {
    overline: "Trending this week",
    titleA: "Rising stars,",
    titleB: "nation-by-nation",
    subtitle: "The three most-viewed verified players per country over the past 7 days. Updated live.",
    mostViewed: "Most viewed · verified only",
    viewsThisWeek: "{{count}} views this week",
    poster: "Download poster",
    posterHint: "Instagram-ready 1080×1350 PNG",
  },
  players: {
    title: "Discover players",
    country: "Any country",
    position: "Any position",
    search: "Search",
    empty: "No players yet.",
    verifiedOnly: "Verified only",
    verifiedOnlyHint: "Hide anyone still awaiting review",
    resultsCount: "{{count}} players",
  },
  profile: {
    verified: "Verified",
    verifiedTooltip: "Verified by AfriGoall",
    shareCard: "Share card",
    downloadCard: "Download PNG",
    vitals: "Vitals",
    radar: "Physical radar",
    highlights: "Highlights",
    reports: "Scouting reports",
    passport: "Passport",
    academy: "Academy",
    dominantFoot: "Dominant foot",
    height: "Height", weight: "Weight",
    speed: "Speed",
    bio: "Bio",
    inviteToTrial: "Invite to trial",
    messagePlayer: "Message player",
    noProfile: "This player hasn't finished their profile yet.",
    noHighlights: "No highlights uploaded yet.",
    heroAlt: "African football player",
  },
  auth: {
    signInTitle: "Sign in", createAccountTitle: "Create account",
    email: "Email", password: "Password", passwordHint: "Min 6 characters",
    forgotPassword: "Forgot password?",
    haveAccount: "Already registered?",
    noAccount: "Need an account?",
    signIn: "Sign in", signInLoading: "Signing in…",
    createAccount: "Create account", creating: "Creating…",
    signInWithGoogle: "Continue with Google",
  },
  register: {
    joinTitle: "Join AfriGoall",
    createAccount: "Create account",
    fullName: "Full name",
    role: "Role",
    rolePlayer: "Player", rolePlayerDesc: "Build a scouting-grade profile",
    roleScout: "Scout / Club", roleScoutDesc: "Discover verified African talent",
    roleCoach: "Coach / Academy", roleCoachDesc: "Verify and rate players",
    proTip: "Pro Tip · Get scouted faster",
    proTipHeadshot: "Clean headshot",
    proTipHeadshotBody: "only you in the photo, well-lit, facing the camera. No group photos.",
    proTipReel: "Highlight reel link",
    proTipReelBody: "(YouTube / Vimeo) is required for scout visibility.",
    phone: "Phone",
    heardAbout: "How did you hear about us? *",
    heardAboutPlaceholder: "Choose a source",
    referralSource: {
      instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook",
      referral: "Friend / Referral", other: "Other",
    },
    isMinor: "I am under 18 — provide parent/guardian contact",
    guardianName: "Guardian full name",
    guardianPhone: "Guardian phone",
    guardianEmail: "Guardian email",
    errorReferral: "Please tell us how you heard about AfriGoall",
    errorCountry: "Please pick your country",
    successToast: "Account created!",
  },
  playerDashboard: {
    title: "My Dashboard",
    overline: "Player Dashboard",
    profileTab: "Profile", videosTab: "Videos", ratingsTab: "Ratings",
    inviteTab: "Invitations", pathwayTab: "Pathway",
    photo: "Profile photo",
    highlights: "Highlight reel link",
    uploadVideo: "Upload video",
    verifiedNote: "Your profile is verified · gold badge live",
    unverifiedNote: "Awaiting verification. Coaches and admins can approve your profile once complete.",
    saved: "Profile saved",
  },
  editProfile: {
    button: "Edit personal info",
    title: "Edit personal info",
    subtitle: "Update your name, phone number and location. This is separate from your player scouting profile.",
    saved: "Saved",
    saving: "Saving…",
    savedToast: "Personal info updated",
  },
  scoutDashboard: {
    title: "Discover Talent",
    overline: "Scout Dashboard",
    discoverTab: "Discover",
    invitesTab: "My Invites",
    relocationTab: "Relocation",
    filter: {
      country: "Country", position: "Position", foot: "Foot", any: "Any",
      minAge: "Min age", maxAge: "Max age", minHeight: "Min height (cm)",
      minSpeed: "Min speed (km/h)", minStamina: "Min stamina",
    },
    inviteBtn: "Invite to trial",
    trendingHeading: "Trending this week",
    trendingSub: "Most viewed · verified only",
    resultsHeading_one: "{{count}} player",
    resultsHeading_other: "{{count}} players",
  },
  admin: {
    title: "Admin Dashboard",
    tabUsers: "Users", tabInquiries: "Inquiries",
    verifyPlayer: "Verify Player",
    verifyBody: "Activates the gold verification badge on the player's profile and lifts them to the top of scout search results.",
    verifyOn: "Verified", verifyOff: "Off",
    notesTitle: "Verification Notes · Private",
    notesHint: "Details from video interviews or background checks. Only admins can see this.",
    notesPlaceholder: "e.g. Video interview Feb 3: confirmed age via passport, plays for XYZ academy since 2022, guardian verified…",
    notesSave: "Save notes",
    sendDigest: "Send trend digest now",
    digestSuccess: "Digest sent — {{sent}} emails, {{skipped}} skipped, {{noData}} without data.",
  },
  footer: {
    tagline: "The verified bridge between African youth football talent and professional clubs worldwide.",
    product: "Product", company: "Company", legal: "Legal",
    linkPlayers: "Players", linkPricing: "Pricing", linkPathway: "Pathway",
    linkAbout: "About", linkContact: "Contact",
    linkTerms: "Terms", linkPrivacy: "Privacy",
    rights: "© {{year}} AfriGoall — All rights reserved.",
    country: "Country", position: "Position",
  },
  pwa: {
    title: "Install AfriGoall",
    body: "Get faster reopens and a full-screen app on your home screen.",
    installBtn: "Install app",
    iosBody: "Tap the Share icon in Safari, then choose Add to Home Screen.",
  },
};

const fr = {
  common: {
    save: "Enregistrer", cancel: "Annuler", submit: "Valider", search: "Rechercher", loading: "Chargement…",
    delete: "Supprimer", edit: "Modifier", back: "Retour", next: "Suivant", close: "Fermer", yes: "Oui", no: "Non",
    optional: "Facultatif", required: "Obligatoire", or: "ou", verified: "Vérifié",
    country: "Pays", city: "Ville", position: "Poste", age: "Âge",
    playersCount_one: "{{count}} joueur", playersCount_other: "{{count}} joueurs",
    viewsCount: "{{count}} vues",
  },
  nav: {
    messages: "Messages", players: "Joueurs", pricing: "Tarifs", pathway: "Parcours",
    about: "À propos", signIn: "Se connecter", join: "S'inscrire", logout: "Déconnexion",
    dashboard: "Tableau de bord",
  },
  hero: {
    badge: "Football africain · portée mondiale",
    line1: "Du terrain",
    line2: "à",
    lagos: "Lagos",
    line3: "au maillot de",
    madrid: "Madrid",
    subtitle: "AfriGoall est le pont vérifié entre les jeunes talents du football africain et les clubs professionnels du monde entier. Créez un profil digne d'un recruteur en quelques minutes.",
    ctaPlayer: "Je suis joueur",
    ctaScout: "Je suis recruteur / club",
    statNations: "Nations",
    statVerified: "Vérifiés",
    statClubs: "Clubs partenaires",
    featuredOverline: "Talent à la une",
    featuredName: "Ibrahim K. — 17 ans",
    featuredRole: "Ailier droit · Ghana",
  },
  landing: {
    valueOverline: "Pourquoi AfriGoall",
    valueTitle: "Des données vérifiées. Prêtes pour les recruteurs.",
    valueSub: "Chaque profil est validé par un coach et rempli des statistiques physiques, techniques et académiques que les recruteurs utilisent réellement.",
    valueCard1Title: "Temps forts vidéo",
    valueCard1Body: "Téléversez vos meilleurs extraits. Chaque profil se lit comme une bande de recrutement.",
    valueCard2Title: "Vérifié par les coachs",
    valueCard2Body: "Les coachs et académies évaluent les performances techniques, physiques et académiques.",
    valueCard3Title: "Parcours de développement",
    valueCard3Body: "Découverte → Essai → Visa / Relocation → Contrat. Structuré et transparent.",
    countriesOverline: "Pays phares",
    countriesTitle: "Le talent sur tout le continent.",
    countriesSub: "Les profils vérifiés arrivent déjà de ces nations.",
    testimonial1: '"Un pont entre le talent africain et le monde." — Chef recruteur, La Liga',
    testimonial2: '"La couche de vérification a tout changé." — Directeur d\'académie, Ghana',
    testimonial3: '"Enfin un vrai pipeline hors d\'Afrique de l\'Ouest." — Chef recruteur, Bundesliga',
    testimonial4: '"Les temps forts et le radar font gagner des semaines." — Talent ID, Ligue 1',
    testimonial5: '"Fait en Afrique, regardé par le monde." — Président de fédération',
  },
  radar: {
    Speed: "Vitesse", Stamina: "Endurance", Strength: "Puissance",
    Dribble: "Dribble", Shoot: "Tir", Pass: "Passe", Defend: "Défense",
  },
  toast: {
    cardFailed: "Échec de génération de la carte",
    shareUnsupported: "Partage non pris en charge — essayez Télécharger",
    playerFallback: "Joueur",
    africaFallback: "Afrique",
    shareText: "Découvrez {{name}} — {{position}} originaire de {{country}} sur AfriGoall.",
    shareTitle: "{{name}} sur AfriGoall",
  },
  pathway: {
    overline: "Le parcours de développement",
    title: "Un vrai pipeline.",
    subtitle: "De la découverte au contrat professionnel.",
    body: "Chaque joueur progresse à travers un parcours structuré. Cette approche protège les joueurs, réduit les risques financiers et donne aux clubs des données vérifiées à chaque étape.",
    step1: "Découverte",
    step1Body: "Construisez un profil digne d'un recruteur avec vidéos, statistiques et validations.",
    step2: "Essai",
    step2Body: "Les recruteurs invitent les joueurs vérifiés à des essais officiels — enregistrés et suivis.",
    step3: "Visa / Relocation",
    step3Body: "Démarches administratives, examens médicaux et voyage — coordonnés sur la plateforme.",
    step4: "Contrat",
    step4Body: "Les clubs signent. Les commissions de succès finalisent le pipeline en toute transparence.",
  },
  cta: {
    overline: "Fait pour un continent",
    title: "Votre talent mérite une scène mondiale.",
    createProfile: "Créer votre profil",
    browsePlayers: "Voir les joueurs",
  },
  contact: {
    title: "Contactez-nous",
    subtitle: "Questions, partenariats ou presse ? Envoyez-nous un message.",
    reachOut: "Écrivez-nous.",
    name: "Nom complet", email: "E-mail", subject: "Sujet", message: "Message",
    send: "Envoyer le message", sending: "Envoi…",
    success: "Message envoyé — nous vous répondrons.",
  },
  spotlights: {
    overline: "Pays à l'honneur",
    titleA: "Talent vérifié,",
    titleB: "par nation",
    subtitle: "Les meilleurs profils vérifiés d'AfriGoall, regroupés par pays d'origine. Touchez un joueur pour voir sa fiche complète.",
    verifiedFrom: "Vérifiés depuis",
    seeAll: "Voir tout",
  },
  trending: {
    overline: "Tendances de la semaine",
    titleA: "Étoiles montantes,",
    titleB: "pays par pays",
    subtitle: "Les trois joueurs vérifiés les plus vus par pays au cours des 7 derniers jours. Mis à jour en direct.",
    mostViewed: "Les plus vus · vérifiés uniquement",
    viewsThisWeek: "{{count}} vues cette semaine",
    poster: "Télécharger l'affiche",
    posterHint: "PNG 1080×1350 prêt pour Instagram",
  },
  players: {
    title: "Découvrir les joueurs",
    country: "Tout pays",
    position: "Tout poste",
    search: "Rechercher",
    empty: "Aucun joueur pour l'instant.",
    verifiedOnly: "Vérifiés uniquement",
    verifiedOnlyHint: "Masquer les profils en attente de validation",
    resultsCount: "{{count}} joueurs",
  },
  profile: {
    verified: "Vérifié",
    verifiedTooltip: "Vérifié par AfriGoall",
    shareCard: "Partager la carte",
    downloadCard: "Télécharger PNG",
    vitals: "Données",
    radar: "Radar physique",
    highlights: "Temps forts",
    reports: "Rapports de scouting",
    passport: "Passeport",
    academy: "Académie",
    dominantFoot: "Pied fort",
    height: "Taille", weight: "Poids",
    speed: "Vitesse",
    bio: "Bio",
    inviteToTrial: "Inviter à un essai",
    messagePlayer: "Envoyer un message",
    noProfile: "Ce joueur n'a pas encore complété son profil.",
    noHighlights: "Aucun temps fort téléversé pour l'instant.",
    heroAlt: "Joueur de football africain",
  },
  auth: {
    signInTitle: "Connexion", createAccountTitle: "Créer un compte",
    email: "E-mail", password: "Mot de passe", passwordHint: "Min. 6 caractères",
    forgotPassword: "Mot de passe oublié ?",
    haveAccount: "Déjà inscrit ?",
    noAccount: "Pas encore de compte ?",
    signIn: "Se connecter", signInLoading: "Connexion…",
    createAccount: "Créer un compte", creating: "Création…",
    signInWithGoogle: "Continuer avec Google",
  },
  register: {
    joinTitle: "Rejoindre AfriGoall",
    createAccount: "Créer un compte",
    fullName: "Nom complet",
    role: "Rôle",
    rolePlayer: "Joueur", rolePlayerDesc: "Construire un profil pour recruteurs",
    roleScout: "Recruteur / Club", roleScoutDesc: "Découvrir des talents africains vérifiés",
    roleCoach: "Coach / Académie", roleCoachDesc: "Valider et évaluer les joueurs",
    proTip: "Conseil pro · Être repéré plus vite",
    proTipHeadshot: "Photo portrait nette",
    proTipHeadshotBody: "seul(e) sur la photo, bien éclairé(e), face à l'objectif. Pas de photos de groupe.",
    proTipReel: "Lien vidéo de temps forts",
    proTipReelBody: "(YouTube / Vimeo) obligatoire pour la visibilité auprès des recruteurs.",
    phone: "Téléphone",
    heardAbout: "Comment nous avez-vous connus ? *",
    heardAboutPlaceholder: "Choisir une source",
    referralSource: {
      instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook",
      referral: "Ami / Recommandation", other: "Autre",
    },
    isMinor: "J'ai moins de 18 ans — fournir un contact parent/tuteur",
    guardianName: "Nom complet du tuteur",
    guardianPhone: "Téléphone du tuteur",
    guardianEmail: "E-mail du tuteur",
    errorReferral: "Dites-nous comment vous nous avez connus",
    errorCountry: "Veuillez choisir votre pays",
    successToast: "Compte créé !",
  },
  playerDashboard: {
    title: "Mon tableau de bord",
    overline: "Tableau du joueur",
    profileTab: "Profil", videosTab: "Vidéos", ratingsTab: "Évaluations",
    inviteTab: "Invitations", pathwayTab: "Parcours",
    photo: "Photo de profil",
    highlights: "Lien de vidéo de temps forts",
    uploadVideo: "Téléverser une vidéo",
    verifiedNote: "Votre profil est vérifié · badge or actif",
    unverifiedNote: "En attente de validation. Les coachs et administrateurs peuvent approuver votre profil une fois complet.",
    saved: "Profil enregistré",
  },
  editProfile: {
    button: "Modifier mes infos",
    title: "Modifier mes infos personnelles",
    subtitle: "Mettez à jour votre nom, votre téléphone et votre localisation. Distinct de votre profil de scouting.",
    saved: "Enregistré",
    saving: "Enregistrement…",
    savedToast: "Informations personnelles mises à jour",
  },
  scoutDashboard: {
    title: "Découvrir des talents",
    overline: "Tableau du recruteur",
    discoverTab: "Découverte",
    invitesTab: "Mes invitations",
    relocationTab: "Relocation",
    filter: {
      country: "Pays", position: "Poste", foot: "Pied fort", any: "Tous",
      minAge: "Âge min.", maxAge: "Âge max.", minHeight: "Taille min. (cm)",
      minSpeed: "Vitesse min. (km/h)", minStamina: "Endurance min.",
    },
    inviteBtn: "Inviter à un essai",
    trendingHeading: "Tendances de la semaine",
    trendingSub: "Les plus vus · vérifiés uniquement",
    resultsHeading_one: "{{count}} joueur",
    resultsHeading_other: "{{count}} joueurs",
  },
  admin: {
    title: "Administration",
    tabUsers: "Utilisateurs", tabInquiries: "Messages reçus",
    verifyPlayer: "Vérifier le joueur",
    verifyBody: "Active le badge or de vérification sur le profil du joueur et le remonte en haut des résultats des recruteurs.",
    verifyOn: "Vérifié", verifyOff: "Désactivé",
    notesTitle: "Notes de vérification · Privées",
    notesHint: "Détails des entretiens vidéo ou vérifications. Visibles uniquement par les admins.",
    notesPlaceholder: "ex. Entretien vidéo du 3 févr. : âge confirmé par passeport, évolue à l'académie XYZ depuis 2022, tuteur vérifié…",
    notesSave: "Enregistrer les notes",
    sendDigest: "Envoyer le digest tendances",
    digestSuccess: "Digest envoyé — {{sent}} e-mails, {{skipped}} ignorés, {{noData}} sans données.",
  },
  footer: {
    tagline: "Le pont vérifié entre les jeunes talents du football africain et les clubs professionnels du monde entier.",
    product: "Produit", company: "Entreprise", legal: "Mentions légales",
    linkPlayers: "Joueurs", linkPricing: "Tarifs", linkPathway: "Parcours",
    linkAbout: "À propos", linkContact: "Contact",
    linkTerms: "Conditions", linkPrivacy: "Confidentialité",
    rights: "© {{year}} AfriGoall — Tous droits réservés.",
    country: "Pays", position: "Poste",
  },
  pwa: {
    title: "Installer AfriGoall",
    body: "Ouvrez plus vite l'app en plein écran depuis votre écran d'accueil.",
    installBtn: "Installer l'app",
    iosBody: "Touchez l'icône de partage dans Safari, puis choisissez « Sur l'écran d'accueil ».",
  },
};

const es = {
  common: {
    save: "Guardar", cancel: "Cancelar", submit: "Enviar", search: "Buscar", loading: "Cargando…",
    delete: "Eliminar", edit: "Editar", back: "Volver", next: "Siguiente", close: "Cerrar", yes: "Sí", no: "No",
    optional: "Opcional", required: "Obligatorio", or: "o", verified: "Verificado",
    country: "País", city: "Ciudad", position: "Posición", age: "Edad",
    playersCount_one: "{{count}} jugador", playersCount_other: "{{count}} jugadores",
    viewsCount: "{{count}} vistas",
  },
  nav: {
    messages: "Mensajes", players: "Jugadores", pricing: "Precios", pathway: "Ruta",
    about: "Acerca de", signIn: "Iniciar sesión", join: "Únete", logout: "Cerrar sesión",
    dashboard: "Panel",
  },
  hero: {
    badge: "Fútbol africano · alcance global",
    line1: "Del campo",
    line2: "en",
    lagos: "Lagos",
    line3: "a una camiseta en",
    madrid: "Madrid",
    subtitle: "AfriGoall es el puente verificado entre el talento juvenil del fútbol africano y los clubes profesionales de todo el mundo. Crea un perfil de nivel scouting en minutos.",
    ctaPlayer: "Soy jugador",
    ctaScout: "Soy scout / club",
    statNations: "Naciones",
    statVerified: "Verificados",
    statClubs: "Clubes asociados",
    featuredOverline: "Talento destacado",
    featuredName: "Ibrahim K. — 17 años",
    featuredRole: "Extremo derecho · Ghana",
  },
  landing: {
    valueOverline: "Por qué AfriGoall",
    valueTitle: "Datos verificados. Listos para scouts.",
    valueSub: "Cada perfil está aprobado por un entrenador y lleva las estadísticas físicas, técnicas y académicas que los scouts realmente usan.",
    valueCard1Title: "Momentos destacados",
    valueCard1Body: "Sube tus mejores clips. Cada perfil se reproduce como un reel de scouting.",
    valueCard2Title: "Verificado por entrenadores",
    valueCard2Body: "Entrenadores y academias evalúan el rendimiento técnico, físico y académico.",
    valueCard3Title: "Ruta de desarrollo",
    valueCard3Body: "Descubrimiento → Prueba → Visa / Reubicación → Contrato. Estructurada y transparente.",
    countriesOverline: "Países destacados",
    countriesTitle: "Talento por todo el continente.",
    countriesSub: "Los perfiles verificados ya llegan desde estas naciones.",
    testimonial1: '"Un puente entre el talento africano y el mundo." — Head Scout, La Liga',
    testimonial2: '"La verificación lo cambió todo." — Director de academia, Ghana',
    testimonial3: '"Por fin un pipeline serio desde África Occidental." — Chief Scout, Bundesliga',
    testimonial4: '"Reels y radar ahorran semanas de trabajo." — Talent ID, Ligue 1',
    testimonial5: '"Hecho en África, visto por el mundo." — Presidente de federación',
  },
  radar: {
    Speed: "Velocidad", Stamina: "Resistencia", Strength: "Fuerza",
    Dribble: "Regate", Shoot: "Tiro", Pass: "Pase", Defend: "Defensa",
  },
  toast: {
    cardFailed: "No se pudo generar la tarjeta",
    shareUnsupported: "Compartir no compatible — usa Descargar",
    playerFallback: "Jugador",
    africaFallback: "África",
    shareText: "Mira a {{name}} — {{position}} de {{country}} en AfriGoall.",
    shareTitle: "{{name}} en AfriGoall",
  },
  pathway: {
    overline: "La ruta de desarrollo",
    title: "Una vía seria.",
    subtitle: "Del descubrimiento al contrato profesional.",
    body: "Cada jugador avanza por una ruta escalonada. Esta estructura protege a los jugadores, reduce el riesgo financiero y ofrece a los clubes datos verificados en cada etapa.",
    step1: "Descubrimiento",
    step1Body: "Construye un perfil de nivel scout con vídeo, estadísticas y verificaciones.",
    step2: "Prueba",
    step2Body: "Los scouts invitan a jugadores verificados a pruebas formales — registradas y rastreadas.",
    step3: "Visa / Reubicación",
    step3Body: "Trámites guiados, exámenes médicos y viaje — coordinados en la plataforma.",
    step4: "Contrato",
    step4Body: "Los clubes firman. Las comisiones de éxito cierran el pipeline con transparencia.",
  },
  cta: {
    overline: "Hecho para un continente",
    title: "Tu talento merece un escenario global.",
    createProfile: "Crea tu perfil",
    browsePlayers: "Ver jugadores",
  },
  contact: {
    title: "Contáctanos",
    subtitle: "¿Preguntas, alianzas o prensa? Envíanos un mensaje.",
    reachOut: "Escríbenos.",
    name: "Nombre completo", email: "Correo", subject: "Asunto", message: "Mensaje",
    send: "Enviar mensaje", sending: "Enviando…",
    success: "Mensaje enviado — te responderemos pronto.",
  },
  spotlights: {
    overline: "Países destacados",
    titleA: "Talento verificado,",
    titleB: "por nación",
    subtitle: "Los mejores perfiles verificados de AfriGoall, agrupados por su país de origen. Toca cualquier jugador para ver su ficha completa.",
    verifiedFrom: "Verificados desde",
    seeAll: "Ver todos",
  },
  trending: {
    overline: "Tendencias de la semana",
    titleA: "Estrellas en ascenso,",
    titleB: "nación por nación",
    subtitle: "Los tres jugadores verificados más vistos por país en los últimos 7 días. Actualizado en vivo.",
    mostViewed: "Más vistos · solo verificados",
    viewsThisWeek: "{{count}} vistas esta semana",
    poster: "Descargar póster",
    posterHint: "PNG 1080×1350 listo para Instagram",
  },
  players: {
    title: "Descubrir jugadores",
    country: "Cualquier país",
    position: "Cualquier posición",
    search: "Buscar",
    empty: "Aún no hay jugadores.",
    verifiedOnly: "Solo verificados",
    verifiedOnlyHint: "Ocultar perfiles pendientes de revisión",
    resultsCount: "{{count}} jugadores",
  },
  profile: {
    verified: "Verificado",
    verifiedTooltip: "Verificado por AfriGoall",
    shareCard: "Compartir tarjeta",
    downloadCard: "Descargar PNG",
    vitals: "Datos",
    radar: "Radar físico",
    highlights: "Momentos destacados",
    reports: "Informes de scouting",
    passport: "Pasaporte",
    academy: "Academia",
    dominantFoot: "Pie hábil",
    height: "Altura", weight: "Peso",
    speed: "Velocidad",
    bio: "Bio",
    inviteToTrial: "Invitar a prueba",
    messagePlayer: "Enviar mensaje",
    noProfile: "Este jugador aún no ha completado su perfil.",
    noHighlights: "Aún no hay momentos destacados subidos.",
    heroAlt: "Jugador de fútbol africano",
  },
  auth: {
    signInTitle: "Iniciar sesión", createAccountTitle: "Crear cuenta",
    email: "Correo", password: "Contraseña", passwordHint: "Mín. 6 caracteres",
    forgotPassword: "¿Olvidaste tu contraseña?",
    haveAccount: "¿Ya tienes cuenta?",
    noAccount: "¿Aún no tienes cuenta?",
    signIn: "Iniciar sesión", signInLoading: "Iniciando…",
    createAccount: "Crear cuenta", creating: "Creando…",
    signInWithGoogle: "Continuar con Google",
  },
  register: {
    joinTitle: "Únete a AfriGoall",
    createAccount: "Crear cuenta",
    fullName: "Nombre completo",
    role: "Rol",
    rolePlayer: "Jugador", rolePlayerDesc: "Crear un perfil de nivel scouting",
    roleScout: "Scout / Club", roleScoutDesc: "Descubrir talento africano verificado",
    roleCoach: "Entrenador / Academia", roleCoachDesc: "Verificar y evaluar jugadores",
    proTip: "Consejo pro · Que te fichen antes",
    proTipHeadshot: "Foto de retrato limpia",
    proTipHeadshotBody: "sólo tú en la foto, bien iluminado, mirando a la cámara. Sin fotos de grupo.",
    proTipReel: "Enlace a reel de momentos",
    proTipReelBody: "(YouTube / Vimeo) obligatorio para visibilidad ante scouts.",
    phone: "Teléfono",
    heardAbout: "¿Cómo nos conociste? *",
    heardAboutPlaceholder: "Elige una fuente",
    referralSource: {
      instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook",
      referral: "Amigo / Recomendación", other: "Otro",
    },
    isMinor: "Soy menor de 18 — proporcionar contacto de padre/tutor",
    guardianName: "Nombre completo del tutor",
    guardianPhone: "Teléfono del tutor",
    guardianEmail: "Correo del tutor",
    errorReferral: "Cuéntanos cómo nos conociste",
    errorCountry: "Elige tu país",
    successToast: "¡Cuenta creada!",
  },
  playerDashboard: {
    title: "Mi panel",
    overline: "Panel del jugador",
    profileTab: "Perfil", videosTab: "Vídeos", ratingsTab: "Valoraciones",
    inviteTab: "Invitaciones", pathwayTab: "Ruta",
    photo: "Foto de perfil",
    highlights: "Enlace de reel de momentos",
    uploadVideo: "Subir vídeo",
    verifiedNote: "Tu perfil está verificado · insignia dorada activa",
    unverifiedNote: "Esperando verificación. Entrenadores y admins pueden aprobarlo cuando esté completo.",
    saved: "Perfil guardado",
  },
  editProfile: {
    button: "Editar datos personales",
    title: "Editar datos personales",
    subtitle: "Actualiza tu nombre, teléfono y ubicación. Es independiente de tu perfil de scouting.",
    saved: "Guardado",
    saving: "Guardando…",
    savedToast: "Datos personales actualizados",
  },
  scoutDashboard: {
    title: "Descubrir talento",
    overline: "Panel del scout",
    discoverTab: "Descubrir",
    invitesTab: "Mis invitaciones",
    relocationTab: "Reubicación",
    filter: {
      country: "País", position: "Posición", foot: "Pie hábil", any: "Cualquiera",
      minAge: "Edad mín.", maxAge: "Edad máx.", minHeight: "Altura mín. (cm)",
      minSpeed: "Velocidad mín. (km/h)", minStamina: "Resistencia mín.",
    },
    inviteBtn: "Invitar a prueba",
    trendingHeading: "Tendencias de la semana",
    trendingSub: "Más vistos · solo verificados",
    resultsHeading_one: "{{count}} jugador",
    resultsHeading_other: "{{count}} jugadores",
  },
  admin: {
    title: "Administración",
    tabUsers: "Usuarios", tabInquiries: "Consultas",
    verifyPlayer: "Verificar jugador",
    verifyBody: "Activa la insignia dorada de verificación en el perfil y lo eleva a lo alto de los resultados.",
    verifyOn: "Verificado", verifyOff: "Desactivado",
    notesTitle: "Notas de verificación · Privadas",
    notesHint: "Detalles de entrevistas en vídeo o verificaciones. Sólo admins pueden verlas.",
    notesPlaceholder: "p. ej. Entrevista en vídeo 3 feb: edad confirmada por pasaporte, juega en la academia XYZ desde 2022, tutor verificado…",
    notesSave: "Guardar notas",
    sendDigest: "Enviar digest ahora",
    digestSuccess: "Digest enviado — {{sent}} correos, {{skipped}} omitidos, {{noData}} sin datos.",
  },
  footer: {
    tagline: "El puente verificado entre el talento juvenil del fútbol africano y los clubes profesionales de todo el mundo.",
    product: "Producto", company: "Empresa", legal: "Legal",
    linkPlayers: "Jugadores", linkPricing: "Precios", linkPathway: "Ruta",
    linkAbout: "Acerca de", linkContact: "Contacto",
    linkTerms: "Términos", linkPrivacy: "Privacidad",
    rights: "© {{year}} AfriGoall — Todos los derechos reservados.",
    country: "País", position: "Posición",
  },
  pwa: {
    title: "Instalar AfriGoall",
    body: "Ábrelo más rápido en pantalla completa desde tu inicio.",
    installBtn: "Instalar app",
    iosBody: "Toca Compartir en Safari y elige 'Añadir a la pantalla de inicio'.",
  },
};

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    es: { translation: es },
  },
  lng: localStorage.getItem("lang") || "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false },
});

export default i18n;
